<template>
  <div v-if="hasData" data-component-id="reprise-table" class="relative">
    <RepriseTableHeaders
      class="app-lightGray"
      :left-table-headers="leftTableHeaders"
      :right-table-headers="rightTableHeaders"
      :sessions="batchedSessions"
      :sort-by="sortBy"
      :is-loading="isLoading"
      @update-sort="updateSortBy"
    />

    <RepriseTableLayout
      class="
        mt-n8
        app-white
      "
      :is-loading="isLoading"
      :session-count="batchedSessions.length"
    >
      <template #left-section>
        <v-data-table
          :headers="leftTableHeaders"
          :items="batchedHorses"
          :items-per-page="-1"
          dense
          hide-default-footer
          class="header-visibility-hidden"
        >
          <template
            v-for="header in leftTableHeaders"
            v-slot:[`item.${header.value}`]="{ item: horse }"
          >
            <div
              :key="`${header.value}-${horse.uuid}`"
              :data-horse-uuid="horse.uuid"
              @mouseenter="highlightHorseRow({ horseUuid: horse.uuid, isEntering: true })"
              @mouseleave="highlightHorseRow({ horseUuid: horse.uuid, isEntering: false })"
              :class="header.innerCellClass"
            >
            <v-tooltip v-if="header.value === 'wtitle' && (horse.wtitle || '').length > 14" bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="text-truncate d-inline-block"
                >{{ horse[header.value] }}</span>
              </template>
              <span>{{ horse[header.value] }}</span>
            </v-tooltip>
            <span v-else class="text-truncate">{{ horse[header.value] }}</span>
            </div>
          </template>
        </v-data-table>
      </template>
      <template #right-section>
        <v-data-table
          id="reprise-table-right"
          :headers="rightTableHeaders"
          :items="batchedHorses"
          :items-per-page="-1"
          dense
          hide-default-footer
          class="header-visibility-hidden"
        >
          <template
            v-for="(session) in batchedSessions"
            v-slot:[`item.${session.uuid}`]="{ item: horse }"
          >
            <div
              :key="session.uuid"
              :data-session-uuid="session.uuid"
              :data-horse-uuid="horse.uuid"
              class="full-height col-12 pa-0"
              @click="activateCell({ session, horse, cellRect: $event.currentTarget.getBoundingClientRect() })"
              @mouseenter="highlightHorseRow({horseUuid: horse.uuid, isEntering: true})"
              @mouseleave="highlightHorseRow({horseUuid: horse.uuid, isEntering: false})"
            >
              <v-list-item
                class="
                  full-height col-12 pa-0 pr-3
                  cursor-text
                "
                style="min-height: 30px; height: 30px; max-height: 30px;"
                inactive
                :ripple="false"
              >
                <v-chip
                  v-if="getAssignedParticipant({ session, horseUuid: horse.uuid })"
                  small
                  color="accent lighten-2"
                  class="
                    mx-1 full-width
                    black--text
                    pointer
                  "
                >
                  <span class="text-truncate">{{ getAssignedParticipant({ session, horseUuid: horse.uuid }).fullname }}</span>
                </v-chip>
                <v-spacer />

                <v-list-item-icon
                  v-if="getAssignedParticipant({ session, horseUuid: horse.uuid })"
                  class="
                    align-self-center
                    ml-1 my-0
                    pointer
                  "
                  @click="onHorseChange({ session, horse, personUuid: null })"
                >
                  <v-icon color="third">mdi-close</v-icon>
                </v-list-item-icon>
                <v-list-item-icon
                  class="
                    align-self-center
                    ml-1 my-0
                    pointer
                  "
                >
                  <v-icon>mdi-menu-down</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </div>
          </template>
        </v-data-table>
      </template>
    </RepriseTableLayout>

    <!-- Extracted autocomplete overlay - avoids table cell recomputation on click/unclick -->
    <v-autocomplete
      v-if="activeCell.session && activeCell.horse && !isLoading"
      ref="autocompleteRef"
      solo
      flat
      clearable
      hide-details
      hide-no-data
      color="third"
      height="0"
      min-height="31px"
      item-text="fullname"
      item-value="uuid"
      :items="listParticipantsAndInstructors(activeCell.session)"
      :value="getAssignedParticipant({ session: activeCell.session, horseUuid: activeCell.horse.uuid }) ? getAssignedParticipant({ session: activeCell.session, horseUuid: activeCell.horse.uuid }).uuid : null"
      :menu-props="{
        contentClass: activeCell.session.uuid && activeCell.horse.uuid ? `highlight-${activeCell.session.uuid}-${activeCell.horse.uuid}` : '',
        offsetOverflow: false,
        'z-index': '500'
      }"
      @change="(personUuid) => onHorseChange({ session: activeCell.session, horse: activeCell.horse, personUuid })"
      @blur="activateCell({ session: null, horse: null, cellRect: null })"
    >
      <template v-slot:selection="{ item }">
        <v-chip small color="accent lighten-2" class="black--text">
          <span class="text-truncate">{{ item.displayName || item.fullname }}</span>
        </v-chip>
      </template>

      <template v-slot:item="{ item }">
        <span class="full-width d-flex justify-space-between align-center gap-2">
          {{ item.fullname }}
          <v-icon
            v-if="item.horse"
            size="20"
            color="grey darken-1"
          >
            mdi-horse-variant
          </v-icon>
        </span>
      </template>
    </v-autocomplete>

    <SwapHorseConfirmationModal />
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeMount, onBeforeUnmount, watch, nextTick } from '@vue/composition-api';
import store from '@/store';

import { isSessionOverlapping, sendNotification, syncRepriseScroll, highlightHorseRow, createBatchRenderer } from '@/helpers/utils';
import { swapHorseSession, removeHorseAndUpdate, NO_UPDATE } from '@/helpers/sessions';

import RepriseTableLayout from '@/components/reprises/table/RepriseTableLayout.vue';
import RepriseTableHeaders from '@/components/reprises/table/RepriseTableHeaders.vue';
import SwapHorseConfirmationModal from '@/components/horse/SwapHorseConfirmationModal.vue';

export default {
  name: 'RepriseTable',
  components: {
    RepriseTableLayout,
    RepriseTableHeaders,
    SwapHorseConfirmationModal
  },
  setup () {
    const isLoading = ref(false);
    const triggerSync = ref(false);
    const sortBy = ref({ column: null, direction: null });
    const activeCell = ref({ sessionUuid: null, horseUuid: null, cellRect: null });
    const autocompleteRef = ref(null);

    const sessions = computed(() => store.getters['sessionsFilters/repriseTab'](store.getters.sortedDailySessions));
    const horses = computed(() => store.getters['horsesFilters/repriseTab'](store.getters.horsesWithEffort));
    const hasData = computed(() => (sessions.value.length && (!store.state.isLazyLoaded.horses || horses.value.length)));

    const batchedHorses = ref([]);
    const batchedSessions = ref([]);

    const horsesRenderer = createBatchRenderer({ targetRef: batchedHorses, batchSize: 20 });
    const sessionRenderer = createBatchRenderer({ targetRef: batchedSessions, batchSize: 5 });

    /* TABLE HEADERS */
    // Inner cell class is used to style the inner cell of the table for highlighting UX
    const leftTableHeaders = [
      { text: 'Chevaux', value: 'wtitle', width: 140, class: 'pr-2 pl-4 py-2 body-2 app-lightGray black--text font-weight-bold', cellClass: 'pa-0', innerCellClass: 'text-truncate font-weight-bold pr-2 pl-4 d-flex align-center full-height'},
      { text: 'Jour', value: 'dailyEffort', class: 'px-1 py-2 body-2 app-lightGray black--text font-weight-bold', cellClass: 'pa-0', innerCellClass: 'pl-2 pr-1 d-flex align-center full-height' },
      { text: 'Semaine', value: 'weeklyEffort', class: 'px-1 py-2 body-2 app-lightGray black--text font-weight-bold', cellClass: 'pa-0', innerCellClass: 'pl-4 pr-1 d-flex align-center full-height' },
      { text: 'Mois', value: 'monthlyEffort', class: 'px-1 py-2 body-2 app-lightGray black--text font-weight-bold', cellClass: 'pa-0', innerCellClass: 'pl-3 px-1 d-flex align-center full-height' }
    ];
    const rightTableHeaders = computed(() => (batchedSessions.value || []).map(session => ({ text: session.name, value: session.uuid, width: 300 + 16, class: 'py-2 body-2 app-lightGray black--text font-weight-bold text-uppercase' })));
    /* END TABLE HEADERS */

    const getAssignedParticipant = ({ session, horseUuid }) => {
      if((session.horses || []).some(horse => horse.uuid === horseUuid && session.participants.every(participant => participant.horse !== horseUuid) && session.instructors.every(instructor => instructor.horse !== horseUuid))) {
        return {
          uuid: 'cheval-sans-cavalier',
          fullname: 'Affecté à la reprise'
        };
      }

      const foundedParticipant = (session.participants || []).find(participant => participant.horse === horseUuid);
      if (foundedParticipant) {
        return { ...foundedParticipant, fullname: `${foundedParticipant?.firstname} ${foundedParticipant?.lastname}` };
      }

      const foundedInstructor = (session.instructors || []).find(instructor => instructor.horse === horseUuid);
      if (foundedInstructor) {
        return { ...foundedInstructor, fullname: `Moniteur: ${foundedInstructor.wtitle}` };
      }

      return null;
    };

    const toggleHorseAssignment = async ({ session, horse, personUuid, overlapSwapConfirmation = false }) => {
      const overlappedSessions = sessions.value.filter(s => s.id !== session.id && isSessionOverlapping(s, session));
      const isHorseAssigned = (overlappedSessions || []).some(s => (s.horses || []).some(h => h.uuid === horse.uuid));
      if(isHorseAssigned && !overlapSwapConfirmation) {
        store.commit('setSwapHorseModal', { show: true, callback: () => toggleHorseAssignment({ session, horse, personUuid, overlapSwapConfirmation: true }) });
        return;
      }
      const horseOnly = !personUuid || personUuid === 'cheval-sans-cavalier';
      let horseWithPerson = { ...horse };

      if(!horseOnly) {
        const fieldsToUpdate = personUuid.startsWith('customer_') ? 'participant' : 'instructor';
        horseWithPerson[fieldsToUpdate] = personUuid;
      }

      const success = await swapHorseSession(overlappedSessions, session, horseWithPerson);
      if(success === NO_UPDATE) return;
      sendNotification(success ? 'success' : 'error', success ? 'Ressource ajoutée avec succès' : 'Erreur lors de l\'ajout de la ressource');
    };

    const onHorseChange = async ({ session, horse, personUuid }) => {
      const isRemove = personUuid == null || personUuid === '';

      isLoading.value = true;
      if (isRemove) {
        const success = await removeHorseAndUpdate(session, horse);
        sendNotification(success ? 'success' : 'error', success ? 'Ressource retirée avec succès' : 'Erreur lors du retrait');
      } else {
        await toggleHorseAssignment({ session, horse, personUuid });
      }
      isLoading.value = false;
    };

    const sortPersons = (persons) => {
      return persons.sort((a, b) => {
        if (a.uuid === 'cheval-sans-cavalier') return 1;
        if (b.uuid === 'cheval-sans-cavalier') return -1;

        // divider comes before cheval-sans-cavalier
        if (a.divider) return 1;
        if (b.divider) return -1;

        if(a?.horse && !b?.horse) return -1;
        if(!a?.horse && b?.horse) return 1;

        return a.fullname.localeCompare(b.fullname, undefined, { sensitivity: 'base' });
      });
    };

    const sortedParticipants = (session) => sortPersons((session.participants || []).map(participant => ({ ...participant, fullname: `${participant.firstname} ${participant.lastname}` })));

    const sortedInstructors = (session) => sortPersons((session.instructors || []).map(instructor => ({...instructor, fullname: `Moniteur: ${instructor.wtitle}`})));

    const listParticipantsAndInstructors = (session) => {
      let list = [];

      const participants = sortedParticipants(session);
      if(participants.length) {
        list.push(...participants);
        list.push({ divider: true });
      }

      const instructors = sortedInstructors(session);
      if(instructors.length) {
        list.push(...instructors);
        list.push({ divider: true });
      }

      list.push({ fullname: 'Cheval sans cavalier (affecté à la reprise)', displayName: 'Affecté à la reprise', uuid: 'cheval-sans-cavalier' });
      return list;
    };

    const horsesSortingPriority = ({ session, horse }) => {
      const inSession = (session.horses || []).some(h => h.uuid === horse.uuid);
      if (!inSession) return 4;
      const inParticipants = (session.participants || []).some(p => p.horse === horse.uuid);
      const inInstructors = (session.instructors || []).some(i => i.horse === horse.uuid);
      if (inParticipants) return 1;
      if (!inInstructors) return 2;

      return 3;
    }

    const sortedHorses = computed(() => {
      if (!sortBy.value.column) {
        return horses.value;
      }

      const sorted = [...horses.value].sort((a, b) => {
        const session = (sortBy.value.column || '').startsWith('session_') ? sessions.value.find(s => s.uuid === sortBy.value.column) : null;

        if (session) {
          const aPriority = horsesSortingPriority({ session, horse: a });
          const bPriority = horsesSortingPriority({ session, horse: b });
          if(aPriority !== bPriority) return aPriority - bPriority;

          if (aPriority === 0) {
            const sessionParticipants = sortedParticipants(session);
            const aIndex = sessionParticipants.findIndex(p => p.horse === a.uuid);
            const bIndex = sessionParticipants.findIndex(p => p.horse === b.uuid);
            return aIndex - bIndex;
          }
          return 0;
        }

        const aVal = a[sortBy.value.column];
        const bVal = b[sortBy.value.column];

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (['dailyEffort', 'weeklyEffort', 'monthlyEffort'].includes(sortBy.value.column)) {
          return parseFloat(aVal) - parseFloat(bVal);
        }

        if (['wtitle'].includes(sortBy.value.column)) {
          return (aVal || '').localeCompare(bVal || '', undefined, { sensitivity: 'base' });
        }

        return aVal - bVal;
      });

      return sortBy.value.direction === 'DESC' ? sorted.reverse() : sorted;
    });

    const updateSortBy = ({ column, direction = 'ASC' }) => {
      if (sortBy.value.column === column && sortBy.value.direction === direction) {
        sortBy.value = { column: null, direction: null };
        return;
      }

      sortBy.value = { column, direction };
    };

    const activateCell = async ({ session, horse, cellRect }) => {
      activeCell.value = { session, horse, cellRect };

      if (!activeCell.value.session || !activeCell.value.horse) return;

      await nextTick();

      if (autocompleteRef.value) {
        const { top, left } = cellRect;
        const containerRect = document.querySelector('[data-component-id="reprise-table"]').getBoundingClientRect();


        Object.assign(autocompleteRef.value.$el.style, {
          position: 'absolute',
          top: `${top - containerRect.top}px`,
          left: `${left - containerRect.left}px`,
          display: 'flex',
          alignItems: 'center',
          height: '30px',
          width: '316px'
        });

        autocompleteRef.value.$refs.menu.isActive = true;
      }
    };

    watch(
      () => [horses.value, sortBy.value.column, sortBy.value.direction],
      () => {
        horsesRenderer.render(sortedHorses.value || []);
      },
      { immediate: true }
    );

    watch(
      () => sessions.value,
      () => {
        const scrollContainer = document.querySelector('[data-component-id="table-reprise"]');
        const savedScrollLeft = scrollContainer?.scrollLeft || 0;

        sessionRenderer.render(sessions.value || [], {
          onDone: () => {
            nextTick(() => {
              if (scrollContainer && savedScrollLeft) {
                scrollContainer.scrollLeft = savedScrollLeft;
                syncRepriseScroll(scrollContainer);
              }
            });
          }
        });
      },
      { immediate: true }
    );

    // Sync scroll position once when horses first appear
    watch(triggerSync, async _ => {
      if (!triggerSync.value) return;

      await nextTick(); // Wait for DOM to be updated
      const planner = document.querySelector('[data-component-id="planner-reprise"]');
      if (planner) syncRepriseScroll(planner);

      triggerSync.value = false;
    });

    const closeAutocompleteOnScroll = () => {
      if (activeCell.value?.session && activeCell.value?.horse) {
        activateCell({ session: null, horse: null, cellRect: null });
      }
    };

    onMounted(() => {
      nextTick(() => {
        const scrollListeners = [document.getElementById('contentTypeCalendar'), document.querySelector('[data-component-id="table-reprise"]')]
        scrollListeners.forEach(el => {
          if (el) el.addEventListener('scroll', closeAutocompleteOnScroll);
        });
      });
    });

    onBeforeUnmount(() => {
      const scrollListeners = [document.getElementById('contentTypeCalendar'), document.querySelector('[data-component-id="table-reprise"]')]
      scrollListeners.forEach(el => {
        if (el) el.removeEventListener('scroll', closeAutocompleteOnScroll);
      });

      horsesRenderer.cancel();
      sessionRenderer.cancel();
    });

    onBeforeMount(async () => {
      if (!sessions.value) return;

      isLoading.value = true;
      if (!store.state.isLazyLoaded.horses) {
        await store.dispatch('getHorses');
      }
      isLoading.value = false;
      triggerSync.value = true;
    });

    return {
      isLoading,
      horses,
      sessions,
      batchedHorses,
      batchedSessions,
      leftTableHeaders,
      rightTableHeaders,
      getAssignedParticipant,
      toggleHorseAssignment,
      updateSortBy,
      onHorseChange,
      sortBy,
      highlightHorseRow,
      activeCell,
      autocompleteRef,
      activateCell,
      listParticipantsAndInstructors,
      hasData,
      closeAutocompleteOnScroll
    }
  }
};
</script>

<style>
  [data-component-id="reprise-table"] {
    [data-component-id="reprise-table-layout"] {
      table {
        tr:hover {
          background: inherit !important;
        }
      }


      .v-data-table .v-data-table__wrapper {
        overflow: unset;
      }

      .header-visibility-hidden .v-data-table-header {
        visibility: hidden;
        top: 0;

        th > * {
          display: none;
        }
      }

      #reprise-table-right {
        table {
          overflow: hidden;
          tbody tr:after {
            content: '';
            background: white;
            display: flex;
            height: 32px;
            border-bottom: 1px solid #e0e0e0;
            width: 100%;
          }
          td {
            padding: 0;
          }
        }
      }
    }
    .v-autocomplete {
      border-radius: 0;

      .v-input__control {
        background-color: red;
        height: 30px;
        min-height: 30px;

        [role="combobox"] {
          padding-left: 0 !important;

          .v-chip {
            width: 100%;
            height: 22px;
          }
          .v-select__selections input {
            min-width: 0 !important;
          }
        }
      }
    }
  }
</style>